import React from "react";
import "./index.css";

// ScrollAnimation component (reused from previous components)
class ScrollAnimation extends React.Component {
  state = {
    isVisible: false,
  };

  domRef = React.createRef();

  componentDidMount() {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.setState({ isVisible: true });
        observer.unobserve(this.domRef.current);
      }
    });

    observer.observe(this.domRef.current);
  }

  render() {
    const { children, stagger } = this.props;
    const staggerClass = stagger ? `stagger-${stagger}` : "";

    return (
      <div
        className={`appointment-scroll-animation ${
          this.state.isVisible ? "is-visible" : ""
        } ${staggerClass}`}
        ref={this.domRef}
      >
        {children}
      </div>
    );
  }
}

class Appointment extends React.Component {
  // State initialization without constructor
  state = {
    formData: {
      name: "",
      email: "",
      phone: "",
      businessName: "",
      date: "",
      time: "",
      service: "",
      notes: "",
    },
    errors: {},
    submitted: false,
    buttonText: "Book Now", // NEW
  };

  // Form validation
  validateForm = () => {
    const { formData } = this.state;
    let errors = {};
    let isValid = true;

    // Name validation
    if (!formData.name || formData.name.length < 2) {
      errors.name = "Name must be at least 2 characters";
      isValid = false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Phone validation
    if (!formData.phone || formData.phone.length < 10) {
      errors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    // Date validation
    if (!formData.date) {
      errors.date = "Please select a date";
      isValid = false;
    }

    // Time validation
    if (!formData.time) {
      errors.time = "Please select a time";
      isValid = false;
    }

    // Service validation
    if (!formData.service) {
      errors.service = "Please select a service";
      isValid = false;
    }

    this.setState({ errors });
    return isValid;
  };

  // Handle input changes
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  // Form submission handler
  handleSubmit = async (e) => {
    e.preventDefault();

    if (this.validateForm()) {
      const { formData } = this.state;

      // Change button text to "Booking in Progress..."
      this.setState({ buttonText: "Booking in Progress..." });

      const submission = new FormData();
      submission.append("access_key", "0c0a7733-d67b-4bb5-bb9b-d7ad93d16f66");
      submission.append("name", formData.name);
      submission.append("email", formData.email);
      submission.append("phone", formData.phone);
      submission.append("service", formData.service);
      submission.append("date", formData.date);
      submission.append("time", formData.time);
      submission.append("message", formData.notes);
      submission.append("subject", "New Appointment Booking via Landing Page");

      try {
        const response = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          body: submission,
        });

        const data = await response.json();

        if (data.success) {
          this.setState({
            submitted: true,
            formData: {
              name: "",
              email: "",
              phone: "",
              businessName: "",
              date: "",
              time: "",
              service: "",
              notes: "",
            },
            errors: {},
            buttonText: "Booked Successfully ✅",
          });

          // Reset text and success message after 5 seconds
          setTimeout(() => {
            this.setState({ submitted: false, buttonText: "Book Now" });
          }, 5000);
        } else {
          alert("Submission failed: " + data.message);
          this.setState({ buttonText: "Book Now" });
        }
      } catch (error) {
        console.error("Submission error:", error);
        alert("An error occurred while submitting the form.");
        this.setState({ buttonText: "Book Now" });
      }
    }
  };

  render() {
    const { formData, errors, submitted } = this.state;

    return (
      <section id="appointment" className="appointment-section">
        <div className="appointment-container">
          <ScrollAnimation>
            <div className="appointment-header">
              <h2 className="appointment-title">
                Make an{" "}
                <span className="appointment-title-highlight">Appointment</span>
              </h2>
              <div className="appointment-divider"></div>
              <p className="appointment-subtitle">
                Looking to elevate your business? Schedule a one-on-one
                consultation with us today!
              </p>
            </div>
          </ScrollAnimation>

          <div className="appointment-form-container">
            <ScrollAnimation>
              <div className="appointment-form-card">
                <div className="appointment-form-header">
                  <h3 className="appointment-form-title">
                    Schedule Your Consultation Today
                  </h3>
                  <p className="appointment-form-description">
                    We'll work with you to develop customized strategies that
                    align with your goals.
                  </p>
                </div>

                <form onSubmit={this.handleSubmit} className="appointment-form">
                  <div className="appointment-form-row">
                    <div className="appointment-form-field">
                      <label className="appointment-form-label">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={this.handleChange}
                        placeholder="Your Name"
                        className="appointment-form-input"
                      />
                      {errors.name && (
                        <span className="appointment-form-error">
                          {errors.name}
                        </span>
                      )}
                    </div>

                    <div className="appointment-form-field">
                      <label className="appointment-form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={this.handleChange}
                        placeholder="your.email@example.com"
                        className="appointment-form-input"
                      />
                      {errors.email && (
                        <span className="appointment-form-error">
                          {errors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="appointment-form-row">
                    <div className="appointment-form-field">
                      <label className="appointment-form-label">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={this.handleChange}
                        placeholder="(123) 456-7890"
                        className="appointment-form-input"
                      />
                      {errors.phone && (
                        <span className="appointment-form-error">
                          {errors.phone}
                        </span>
                      )}
                    </div>

                    <div className="appointment-form-field appointment-form-field-full">
                      <label className="appointment-form-label">
                        Service(s) of Interest
                      </label>
                      <div className="appointment-select-wrapper">
                        <select
                          name="service"
                          value={formData.service}
                          onChange={this.handleChange}
                          className="appointment-form-select"
                        >
                          <option value="" disabled>
                            Select a service...
                          </option>
                          <option value="crawling_to_destiny">
                            Crawling to Destiny
                          </option>
                          <option value="behavioral_health_services">
                            New Vision Behavioral Health Services
                          </option>
                          <option value="hitchcock_woods_learning">
                            Hitchcock Woods Learning
                          </option>
                          <option value="youngstown_event_center">
                            Youngstown Event Center
                          </option>
                        </select>
                      </div>
                      {errors.service && (
                        <span className="appointment-form-error">
                          {errors.service}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="appointment-form-row">
                    <div className="appointment-form-field">
                      <label className="appointment-form-label">
                        Preferred Date
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={this.handleChange}
                        className="appointment-form-input"
                        placeholder="mm/dd/yyyy"
                      />
                      {errors.date && (
                        <span className="appointment-form-error">
                          {errors.date}
                        </span>
                      )}
                    </div>

                    <div className="appointment-form-field">
                      <label className="appointment-form-label">
                        Preferred Time
                      </label>
                      <input
                        type="time"
                        name="time"
                        value={formData.time}
                        onChange={this.handleChange}
                        className="appointment-form-input"
                        placeholder="--:-- --"
                      />
                      {errors.time && (
                        <span className="appointment-form-error">
                          {errors.time}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="appointment-form-field appointment-form-field-full">
                    <label className="appointment-form-label">
                      Additional Notes/Questions
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={this.handleChange}
                      placeholder="Tell us more about your needs..."
                      rows={4}
                      className="appointment-form-textarea"
                    ></textarea>
                  </div>

                  <div className="appointment-form-submit">
                    <button type="submit" className="appointment-submit-button">
                      {this.state.buttonText}
                    </button>
                  </div>

                  {submitted && (
                    <div className="appointment-success-message">
                      <p>
                        Appointment scheduled! We'll be in touch soon to confirm
                        your consultation.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>
    );
  }
}

export default Appointment;
